import { Inject, Injectable, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { AwsCredentialsService } from 'src/app/shared/services/aws-credentials/aws-credentials.service';
import { MessageService } from 'src/app/modules/message/services/message/message.service';
import { MessageCodes } from 'src/app/shared/enums';

/**
 * Keep the shared AWS credentials (AwsCredentialsService) warm while the user is logged in
 * so that SigV4 signing never blocks on a cold credentials fetch.
 *
 * This mirrors the proactive-refresh pattern in previously auth-migrated reference apps:
 * a setTimeout comfortably under the credentials TTL and a visibilitychange handler for
 * backgrounded tabs. All refreshes are single-flight via AwsCredentialsService so that
 * overlapping triggers collapse into one call.
 *
 * Call start() when the user is logged in and stop() on logout.
 */
@Injectable({ providedIn: 'root' })
export class CredentialsRefreshService implements OnDestroy {
  /**
   * This is the buffer time in milliseconds before the real credentials expiry
   * at which we consider credentials stale and trigger a proactive refresh.
   * AwsCredentialsService has its own expiry buffer for on-demand refreshes.
   */
  private readonly PROACTIVE_REFRESH_EXPIRY_BUFFER_MS = 10 * 60 * 1000;

  private readonly MAX_REFRESH_FAILURES = 10;
  private readonly RETRY_BASE_MS = 5 * 1000;
  private readonly MAX_BACKOFF_MS = 60 * 1000;

  private timer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private refreshFailures = 0;
  private maxRefreshFailuresReached = false;
  private refreshing = false;
  private readonly onVisibilityChange = (): void =>
    this.handleVisibilityChange();

  constructor(
    private readonly creds: AwsCredentialsService,
    private readonly messageService: MessageService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  /** Begin keeping credentials warm (idempotent and safe to call on every auth event). */
  async start(): Promise<void> {
    if (this.started) {
      return;
    }
    this.started = true;

    this.refreshFailures = 0;
    this.maxRefreshFailuresReached = false;

    this.document.addEventListener('visibilitychange', this.onVisibilityChange);

    await this.refreshAndSchedule();
  }

  /** Stop refreshing and drop cached credentials. */
  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;

    this.clearTimer();

    this.document.removeEventListener(
      'visibilitychange',
      this.onVisibilityChange
    );

    this.creds.invalidate();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  /**
   * Determine if a credentials refresh should be attempted.
   *
   * We stop attempting to refresh credentials when the maximum number of failures is reached,
   * since the current error message we display in that scenario is full-screen/non-dismissible
   * and the intended mechanism for recovery is app reload.
   *
   * @returns true if a credentials refresh should be attempted
   */
  private shouldRefresh(): boolean {
    return this.started && !this.maxRefreshFailuresReached;
  }

  private async refreshAndSchedule(): Promise<void> {
    if (!this.shouldRefresh()) {
      return;
    }

    // Non-reentrant (avoids the timer and visibilitychange event handling both calling this
    // and double-counting a failure on the shared single-flight refresh)
    if (this.refreshing) {
      return;
    }
    this.refreshing = true;

    try {
      await this.creds.refresh();

      if (!this.started) {
        return;
      }

      this.refreshFailures = 0;

      this.scheduleNext();
    } catch (error) {
      if (!this.started) {
        return;
      }

      this.refreshFailures++;

      const message = error instanceof Error ? error.message : String(error);
      if (this.refreshFailures >= this.MAX_REFRESH_FAILURES) {
        this.maxRefreshFailuresReached = true;

        this.clearTimer();

        console.error(
          `CredentialsRefreshService: Credentials refresh failed ${this.refreshFailures} times.`,
          message
        );
        this.messageService.showMessage(MessageCodes.HTTP_UNEXPECTED_NET_ERROR);

        return;
      }

      const delay = this.backoffDelay(this.refreshFailures);
      console.warn(
        `CredentialsRefreshService: Refresh failed (attempt ${this.refreshFailures}/${this.MAX_REFRESH_FAILURES}). Retrying in ${delay} ms.`,
        message
      );
      this.scheduleIn(delay);
    } finally {
      this.refreshing = false;
    }
  }

  /** Capped exponential backoff. */
  private backoffDelay(failures: number): number {
    return Math.min(
      this.RETRY_BASE_MS * 2 ** (failures - 1),
      this.MAX_BACKOFF_MS
    );
  }

  private scheduleNext(): void {
    const expiresAt = this.creds.expiresAt;
    const delay = expiresAt
      ? Math.max(
          expiresAt.getTime() -
            this.PROACTIVE_REFRESH_EXPIRY_BUFFER_MS -
            Date.now(),
          this.RETRY_BASE_MS
        )
      : 45 * 60 * 1000;

    this.scheduleIn(delay);
  }

  private scheduleIn(delay: number): void {
    this.clearTimer();
    this.timer = setTimeout(() => void this.refreshAndSchedule(), delay);
  }

  /**
   * Timers are throttled/suspended in backgrounded tabs. On refocus, force a refresh if
   * we're at or past the buffer before expiry so that we never use stale credentials.
   */
  private handleVisibilityChange(): void {
    if (!this.shouldRefresh() || this.document.visibilityState !== 'visible') {
      return;
    }

    const expiresAt = this.creds.expiresAt;
    if (
      !expiresAt ||
      expiresAt.getTime() - this.PROACTIVE_REFRESH_EXPIRY_BUFFER_MS <=
        Date.now()
    ) {
      void this.refreshAndSchedule();
    }
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
